import { prefersReducedMotion, isLowPowerDevice } from '@theme/utilities';

/**
 * Scroll-reveal controller driven by the `data-reveal` attribute.
 *
 * Design (Phase 3, red-team #2 — morph resistant):
 * - Reveal state is the attribute `data-revealed`, NOT a class. `data-revealed` is
 *   whitelisted in assets/morph.js `PRESERVED_ATTRIBUTES`, so a revealed element keeps
 *   its state across Section-Rendering morphs (facets/sort). Reused nodes therefore never
 *   replay; only genuinely new server markup (missing `data-revealed`) animates in.
 * - The hidden initial state is gated behind `html[data-reveal-active]`, which this module
 *   sets only when motion is allowed. Without JS (or under reduced-motion / low-power) the
 *   attribute is absent and `[data-reveal]` elements stay fully visible — no invisible items.
 * - Attribute-driven (works on any element, e.g. the collection `<ul>` grid) rather than a
 *   wrapper custom element, so it can decorate containers that cannot be re-tagged.
 *
 * Variants: `data-reveal="zoom-out" | "fade-up" | "stagger"` (see assets/base.css).
 */

const REVEAL_ATTRIBUTE = 'data-reveal';
const REVEALED_ATTRIBUTE = 'data-revealed';
const SELECTOR = `[${REVEAL_ATTRIBUTE}]:not([${REVEALED_ATTRIBUTE}])`;

if (!prefersReducedMotion() && !isLowPowerDevice()) {
  // Set synchronously so the hidden initial CSS applies before the first reveal, and never
  // under reduced-motion / low-power (where the attribute stays off and content is visible).
  document.documentElement.setAttribute('data-reveal-active', '');

  /** @type {IntersectionObserver} */
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        // Non-empty value: morph.js onBeforeUpdate only preserves truthy attribute values.
        entry.target.setAttribute(REVEALED_ATTRIBUTE, 'true');
        observer.unobserve(entry.target);
      }
    },
    // Low threshold so items below the fold (revealed after a filter morph) don't stay hidden.
    { threshold: 0.05 }
  );

  /** @param {ParentNode | Element} root */
  const observeWithin = (root) => {
    if (root instanceof Element && root.matches(SELECTOR)) observer.observe(root);
    root.querySelectorAll?.(SELECTOR).forEach((element) => observer.observe(element));
  };

  const start = () => {
    observeWithin(document);

    // New markup injected by Section Rendering (filter/sort, infinite scroll) arrives without
    // `data-revealed`; observe it so only the new items animate.
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) observeWithin(node);
        }
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
